import React, { useCallback, useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import type {
	QuickInsertActionInsert,
	QuickInsertItem,
} from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import { createQuickInsertMatcher } from '@atlaskit/editor-common/quick-insert/create-quick-insert-matcher';
import {
	BLOCK_TEMPLATES_SECTION,
	getBlockTemplateMenuItem,
} from '@atlaskit/editor-common/quick-insert/keys';
import {
	QuickInsertMenuItem,
	type OnSelectContext,
} from '@atlaskit/editor-common/quick-insert/menu-item';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import { getSnippetMenuRank } from '@atlaskit/editor-common/quick-insert/snippet-menu-rank';
import { getSnippetPreviewImageUrls } from '@atlaskit/editor-common/quick-insert/snippet-preview-image-urls';
import { useQuickInsertContext } from '@atlaskit/editor-common/quick-insert/use-quick-insert-context';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertHandlerFn } from '@atlaskit/editor-common/types/quick-insert';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';
import type { UiControlRegistryPlugin } from '@atlaskit/editor-plugin-ui-control-registry/ui-control-registry-plugin-type';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { RegisterMenuItem } from '@atlaskit/editor-ui-control-model/types';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';

import { ANALYTICS_ORIGIN } from '../constants/snippetAnalytics.ts';
import {
	buildInsertSnippetEventData,
	fireSnippetUIEvent,
} from '../hooks/analytics/snippetAnalyticsPayload.ts';
import type { Snippet } from '../types/snippet.ts';
import {
	createSnippetDescription,
	extractSnippetKeywords,
	getSnippetQuickInsertKey,
} from '../utils/quickInsertUtils.ts';

/**
 * Editor API shape used by the quick-insert snippet item.
 *
 * The analytics plugin is optional at runtime, so rather than forcing every
 * caller to provide an `AnalyticsPlugin`-typed API we keep the public shape as
 * the QuickInsert API and read the (optional) analytics plugin defensively.
 */
type SnippetQuickInsertEditorAPI = PublicPluginAPI<[QuickInsertPlugin]> &
	Partial<PublicPluginAPI<[UiControlRegistryPlugin]>>;

/** The editor API as accepted by this module — may be absent at runtime. */
type OptionalSnippetQuickInsertEditorAPI = SnippetQuickInsertEditorAPI | null | undefined;

/**
 * Fires an `insertSnippet` UI event with `origin=quickInsert` using the
 * editor's analytics plugin. No-op if the analytics plugin or its
 * `createAnalyticsEvent` factory is unavailable.
 *
 * `PublicPluginAPI` exposes optional plugin dependencies (such as analytics)
 * as optional properties, so the analytics plugin can be read defensively via
 * optional chaining without a type assertion.
 */
const fireQuickInsertSnippetInsertedAnalytic = (
	editorAPI: OptionalSnippetQuickInsertEditorAPI,
	snippet: Snippet,
) => {
	const analyticsApi = editorAPI?.analytics;
	const createAnalyticsEvent = analyticsApi?.sharedState.currentState()?.createAnalyticsEvent;
	fireSnippetUIEvent(
		createAnalyticsEvent,
		buildInsertSnippetEventData(snippet),
		ANALYTICS_ORIGIN.QUICK_INSERT,
	);
};

const SnippetQuickInsertMenuItem = ({
	editorAPI,
	snippet,
}: {
	editorAPI: OptionalSnippetQuickInsertEditorAPI;
	snippet: Snippet;
}): React.JSX.Element => {
	const intl = useIntl();
	const item = useMemo(
		() => createSnippetQuickInsertItem({ snippet, intl, editorAPI }),
		[editorAPI, intl, snippet],
	);
	const { isOffline, surface } = useQuickInsertContext();
	const preview = item.preview;
	const hasPreviewImage = preview?.image !== undefined;
	const onSelect = useCallback(
		({ editorView, insert, source }: OnSelectContext) =>
			item.action(insert, editorView.state, source),
		[item],
	);

	return (
		<QuickInsertMenuItem
			description={
				hasPreviewImage && surface !== 'element-browser'
					? (snippet.description ?? '')
					: item.description
			}
			iconBefore={item.icon?.()}
			isDisabled={isOffline}
			onSelect={onSelect}
			preview={preview}
			title={item.title}
		/>
	);
};

const getSnippetQuickInsertComponent = ({
	editorAPI,
	parents,
	snippet,
}: {
	editorAPI: OptionalSnippetQuickInsertEditorAPI;
	parents?: RegisterMenuItem['parents'];
	snippet: Snippet;
}): RegisterMenuItem => {
	const menuItem = getBlockTemplateMenuItem(snippet.snippetId);
	const rank = getSnippetMenuRank({
		insertCount: snippet.insertCount,
		isFavorite: snippet.isFavorite,
		scope: snippet.scope,
		updatedAt: snippet.updatedAt.getTime(),
	});

	return {
		key: menuItem.key,
		type: menuItem.type,
		parents: parents
			? parents.map((parent) =>
					parent.key === BLOCK_TEMPLATES_SECTION.key
						? { key: parent.key, type: parent.type, rank }
						: parent,
				)
			: [{ key: BLOCK_TEMPLATES_SECTION.key, type: BLOCK_TEMPLATES_SECTION.type, rank }],
		match: createQuickInsertMatcher(({ formatMessage }) => {
			const item = createSnippetQuickInsertItem({
				snippet,
				intl: { formatMessage } as IntlShape,
				editorAPI,
			});
			return {
				description: item.description,
				keywords: item.keywords,
				shortcut: item.keyshortcut,
				title: item.title,
			};
		}),
		component: () => <SnippetQuickInsertMenuItem editorAPI={editorAPI} snippet={snippet} />,
	};
};

/**
 * Adds a snippet to the quick insert menu using the editor API.
 */
export const addSnippetQuickInsertItem = ({
	snippet,
	editorAPI,
}: {
	snippet: Snippet;
	editorAPI: OptionalSnippetQuickInsertEditorAPI;
}): void => {
	if (!editorAPI) {
		return;
	}
	const registry = editorAPI.uiControlRegistry?.actions;
	if (isExperimentEnabled('platform_editor_slash_command') && registry) {
		const menuItem = getBlockTemplateMenuItem(snippet.snippetId);
		const existing = registry.getComponent({ key: menuItem.key, type: menuItem.type });
		registry.register(
			[
				getSnippetQuickInsertComponent({
					editorAPI,
					parents: existing?.parents as RegisterMenuItem['parents'] | undefined,
					snippet,
				}),
			],
			{ replaceExisting: true },
		);
		return;
	}

	const addQuickInsertItem: ((item: QuickInsertHandlerFn) => EditorCommand) | undefined =
		editorAPI?.quickInsert?.commands?.addQuickInsertItem;

	if (addQuickInsertItem === undefined) {
		return;
	}

	editorAPI.core?.actions.execute(
		addQuickInsertItem((intl: IntlShape) => [
			createSnippetQuickInsertItem({ snippet, intl, editorAPI }),
		]),
	);
};

/**
 * Updates an existing snippet in the editor's quick insert menu (e.g. after edit or share/unshare).
 * No-op if editorAPI is null/undefined or updateQuickInsertItem is not available.
 */
export const updateSnippetInQuickInsertItem = ({
	snippet,
	editorAPI,
}: {
	snippet: Snippet;
	editorAPI: OptionalSnippetQuickInsertEditorAPI;
}): void => {
	if (!editorAPI) {
		return;
	}
	const registry = editorAPI.uiControlRegistry?.actions;
	if (isExperimentEnabled('platform_editor_slash_command') && registry) {
		const menuItem = getBlockTemplateMenuItem(snippet.snippetId);
		const existing = registry.getComponent({ key: menuItem.key, type: menuItem.type });
		registry.register(
			[
				getSnippetQuickInsertComponent({
					editorAPI,
					parents: existing?.parents as RegisterMenuItem['parents'] | undefined,
					snippet,
				}),
			],
			{ replaceExisting: true },
		);
		return;
	}

	const updateQuickInsertItem = editorAPI?.quickInsert?.commands?.updateQuickInsertItem ?? null;
	if (!updateQuickInsertItem) {
		return;
	}
	editorAPI.core?.actions.execute(
		updateQuickInsertItem(getSnippetQuickInsertKey(snippet.snippetId), (intl: IntlShape) => [
			createSnippetQuickInsertItem({ snippet, intl, editorAPI }),
		]),
	);
};

export const removeSnippetQuickInsertItem = ({
	editorAPI,
	snippetId,
}: {
	editorAPI: OptionalSnippetQuickInsertEditorAPI;
	snippetId: string;
}): void => {
	if (!editorAPI) {
		return;
	}

	const registry = editorAPI.uiControlRegistry?.actions;
	if (isExperimentEnabled('platform_editor_slash_command') && registry) {
		const menuItem = getBlockTemplateMenuItem(snippetId);
		registry.unregister([{ key: menuItem.key, type: menuItem.type }]);
		return;
	}

	const removeQuickInsertItem = editorAPI.quickInsert?.commands?.removeQuickInsertItem;
	if (removeQuickInsertItem) {
		editorAPI.core?.actions.execute(removeQuickInsertItem(getSnippetQuickInsertKey(snippetId)));
	}
};

/**
 * Creates a snippet quick insert item.
 *
 * When `editorAPI` is provided (and exposes the analytics plugin), inserting a
 * snippet via quick-insert fires an `insertSnippet` UI event with
 * `origin=quickInsert`, ensuring the Confluence insert entry-point is captured
 * for the quick-insert / block-menu surface (not just the snippet panel).
 */
export const createSnippetQuickInsertItem = ({
	snippet,
	intl,
	editorAPI,
}: {
	snippet: Snippet;
	intl: IntlShape;
	editorAPI?: SnippetQuickInsertEditorAPI | null;
}): QuickInsertItem => {
	const previewImageUrls =
		snippet.scope === SnippetScope.GLOBAL && isExperimentEnabled('platform_editor_slash_command')
			? getSnippetPreviewImageUrls(snippet.snippetId)
			: undefined;

	return {
		key: getSnippetQuickInsertKey(snippet.snippetId),
		...(isExperimentEnabled('platform_editor_slash_command')
			? { category: 'block-templates' }
			: {}),
		title: snippet.name,
		description: createSnippetDescription(snippet.createdBy, intl, snippet.description),
		keywords: extractSnippetKeywords(intl),
		isDisabledOffline: true,
		icon: () => <TemplateIcon label="Snippet" />,
		preview: previewImageUrls
			? {
					image: previewImageUrls,
					attribution: {
						name: intl.formatMessage(quickInsertMessages.previewAttributionAtlassian),
					},
				}
			: undefined,
		action: (insert: QuickInsertActionInsert, state: EditorState) => {
			const body = snippet.body;
			if (!body) {
				return false;
			}

			const result = Array.isArray(body)
				? insert(Fragment.fromJSON(state.schema, body))
				: insert(body);

			if (result) {
				fireQuickInsertSnippetInsertedAnalytic(editorAPI, snippet);
			}

			return result;
		},
	};
};
